/**
 * Pool creation utilities - shared enums, labels, and calculations
 */

import { LoanType, PropertyOccupancyStatus } from '@/types/pool';
import { LoanTypeLabel } from './misc';

export const PROPERTY_OCCUPANCY_CHOICE_OPTIONS: { value: PropertyOccupancyStatus; label: string }[] = [
  { value: PropertyOccupancyStatus.PRIMARY_RESIDENCE, label: 'Owner occupied' },
  { value: PropertyOccupancyStatus.VACANT, label: 'Vacant' },
  { value: PropertyOccupancyStatus.TENANT_OCCUPIED, label: 'Tenant occupied' },
  { value: PropertyOccupancyStatus.OWNER_OCCUPIED, label: "It's owner-occupied (not my primary home)" },
  // TODO -- remove this when backend is updated to not send OWNER_OCCUPIED
];

export const getPropertyOccupancyLabel = (choice: PropertyOccupancyStatus | null): string => {
  return PROPERTY_OCCUPANCY_CHOICE_OPTIONS.find((option) => option.value === choice)?.label || 'N/A';
};

export const LOAN_TYPE_OPTIONS: { value: LoanType; label: string; description: string }[] = [
  {
    value: LoanType.INTEREST_ONLY,
    label: LoanTypeLabel[LoanType.INTEREST_ONLY],
    description: 'Pay only interest each month. Full principal due at the end.',
  },
  {
    value: LoanType.AMORTIZING,
    label: LoanTypeLabel[LoanType.AMORTIZING],
    description: 'Equal monthly payments of principal + interest throughout the term.',
  },
];

export const getLoanTypeLabel = (loanType: LoanType | null): string => {
  return LOAN_TYPE_OPTIONS.find((option) => option.value === loanType)?.label || 'N/A';
};

/**
 * Format term display
 */
export const formatTermDisplay = (termMonths: number): string => {
  return termMonths ? `${termMonths} months` : 'N/A';
};

/**
 * Format currency value with commas, preserving decimals (without $ prefix)
 */
export const formatCurrencyValue = (amount: string | null | undefined): string => {
  if (!amount) return '';
  // Remove any existing formatting (commas, $, spaces) but preserve decimal point
  const numStr = amount.replace(/[,$\s]/g, '');
  if (!numStr) return '';
  const num = parseFloat(numStr);
  if (isNaN(num)) return '';
  // Detect decimal places from original string
  const decimalMatch = numStr.match(/\.(\d+)/);
  const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;
  // Format with commas, preserving decimal places
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

/**
 * Format currency display with commas, preserving decimals
 */
export const formFormatCurrency = (amount: string | null | undefined): string => {
  if (!amount) return 'N/A';
  const formatted = formatCurrencyValue(amount);
  return formatted ? `$${formatted}` : 'N/A';
};

/**
 * Format percentage display
 */
export const formatPercentage = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return `${value}%`;
};
